import React, { useState, useEffect } from 'react';
import { Shield, Users, Calendar, Video, Book, Bell, Settings, Home, BarChart2, Database, FileText, MessageSquare, BookOpenCheck, HelpingHand, Building, Menu, X } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface AdminLocalLayoutProps {
  children: React.ReactNode;
  title: string;
}

interface Igreja {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
}

const menuItems = [
  { icon: BarChart2, label: 'Dashboard', path: '/admin-local' },
  { icon: Users, label: 'Usuários', path: '/admin-local/users' },
  { icon: Calendar, label: 'Eventos', path: '/admin-local/events' },
  { icon: Video, label: 'Transmissões', path: '/admin-local/live' },
  { icon: Book, label: 'Mensagens', path: '/admin-local/messages' },
  { icon: MessageSquare, label: 'Pedidos de Oração', path: '/admin-local/prayers' },
  { icon: HelpingHand, label: 'Orientação Espiritual', path: '/admin-local/spiritual-guidance' },
  { icon: Bell, label: 'Notificações', path: '/admin-local/notifications' },
  { icon: BookOpenCheck, label: 'Dicas Cristãs', path: '/admin-local/christian-tips' },
  { icon: MessageSquare, label: 'Testemunhos', path: '/admin-local/testimonials' },
  { icon: Settings, label: 'Configurações', path: '/admin-local/settings' },
];

export default function AdminLocalLayout({ children, title }: AdminLocalLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [igreja, setIgreja] = useState<Igreja | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIgrejaDoUsuario();
  }, []);

  async function fetchIgrejaDoUsuario() {
    try {
      setLoading(true);
      
      // Obter o usuário atual
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth');
        return;
      }
      
      // Obter a igreja do usuário (assumindo que o ID da igreja está nos metadados do usuário)
      const igreja_id = user.user_metadata?.igreja_id;
      
      if (!igreja_id) {
        console.error('Usuário não possui igreja associada');
        return;
      }
      
      // Buscar dados da igreja
      const { data, error } = await supabase
        .from('igrejas')
        .select('id, nome, cidade, estado')
        .eq('id', igreja_id)
        .single();
      
      if (error) throw error;
      setIgreja(data);
    } catch (error) {
      console.error('Erro ao buscar igreja do usuário:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      <div className="flex">
        {/* Sidebar - Mobile (off-canvas) */}
        <aside 
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <Shield className="h-8 w-8 text-brand-teal" />
                <span className="font-semibold text-gray-900">Admin Local</span>
              </div>
              <button 
                className="lg:hidden text-gray-500 hover:text-gray-700"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Igreja info */}
            {igreja && (
              <div className="mb-6 p-3 bg-brand-light text-brand-teal rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Building className="h-5 w-5" />
                  <span className="font-medium">{igreja.nome}</span>
                </div>
                <div className="text-sm text-brand-teal/80 pl-7">
                  {igreja.cidade}/{igreja.estado}
                </div>
              </div>
            )}

            <nav className="space-y-1">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === item.path
                      ? 'bg-brand-light text-brand-teal'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-4 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button 
                  className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="h-6 w-6" />
                </button>
                <h1 className="text-xl lg:text-2xl font-bold text-brand-teal">{title}</h1>
              </div>
              <div className="flex items-center gap-4">
                {igreja && (
                  <div className="hidden md:block text-right">
                    <p className="text-sm font-medium text-gray-900">{igreja.nome}</p>
                    <p className="text-xs text-gray-500">{igreja.cidade}/{igreja.estado}</p>
                  </div>
                )}
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
                >
                  <Home className="h-4 w-4" />
                  <span className="hidden sm:inline">Voltar para o Site</span>
                </button>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 p-4 lg:p-8 overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
              </div>
            ) : (
              children
            )}
          </main>
        </div>
      </div>
    </div>
  );
}